import { 
  useState, 
  useEffect,
} 
from 'react'
import { 
  useSelector, 
  useDispatch, 
} from 'react-redux'
import { 
  useNavigate,
} from 'react-router-dom'

import { 
  registerUser, 
  reset, 
} from "../features/auth/authSlice"

import {
  Input,
  Text,
  Box,
  Link,
  Checkbox,
} from '@chakra-ui/react'

import Map1Image from '../assets/images/map1-800-600.png'

import { Helmet } from 'react-helmet-async'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import StatusOverlay from '../components/StatusOverlay'
import LoadingOverlay from '../components/LoadingOverlay'

export default function Register() {

  const [showPassword, setShowPassword] = useState(false);

  const handleShowClick = () => setShowPassword(!showPassword);

  const dispatch = useDispatch()

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      promotionalEmailsConsent: false
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .max(40, "Email cannot be longer than 40 characters")
        .required("Email is required")
        .email("Invalid email address"),
      password: Yup.string()
        .min(8, "Password must have a minimum of 8 characters")
        .max(40, "Password cannot be longer than 40 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .min(8, "Password must have a minimum of 8 characters")
        .max(40, "Password cannot be longer than 40 characters")
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required("Password confirmation is required"),
    }),
    onSubmit: (values) => {
      const formData = {
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
        promotionalEmailsConsent: values.promotionalEmailsConsent
      }
      dispatch(registerUser({ formData }))
    }
  })

  const navigate = useNavigate()

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth
  )

  useEffect(() => {

    if (isSuccess || user) {
      navigate('/myjourneys')
    }

    dispatch(reset())
  }, [user, isSuccess, navigate, dispatch])

  if (isLoading) {
    return (
      <>
        <LoadingOverlay type="full" />
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Register | TrainLookout</title>
        <meta name="description" content="Create your TrainLookout account for free today and start logging your rail journeys!" />
        <meta name="keywords" content="register, trainlookout, train, lookout, trainlookout, train lookout, trainlookout.com, trainlookout.com register, trainlookout register, trainlookout create, trainlookout.com login page, trainlookout login page" />
        <link rel="canonical" href="/register" />
      </Helmet>

      {isError && (<StatusOverlay type="top-alert-error" text={`Error: ${message}`} />)}

      <section className="vh-100">
        <div className="container h-100">
          <div className="row d-flex justify-content-center align-items-center h-100">
            <div className="col-lg-12 col-xl-11">
              <div className="card text-black" style={{ borderRadius: '25px' }}>
                <div className="card-body p-md-5">
                  <div className="row justify-content-center">
                    <div className="col-md-10 col-lg-6 col-xl-5 order-2 order-lg-1">
                      <p className="text-center h1 fw-bold mb-5 mx-1 mx-md-4 mt-4">Register</p>
                      <form onSubmit={formik.handleSubmit} className="mx-1 mx-md-4">
                        {(formik.errors.email && formik.touched.email) && (<Box>{formik.errors.email}</Box>)}
                        {(formik.errors.password && formik.touched.password) && (<Box>{formik.errors.password}</Box>)}
                        {(formik.errors.confirmPassword && formik.touched.confirmPassword) && (<Box>{formik.errors.confirmPassword}</Box>)}
                        <div className="d-flex flex-row align-items-center mb-4">
                          <i className="fas fa-envelope fa-lg me-3 fa-fw" />
                          <div className="form-outline flex-fill mb-0">
                            <Input
                              type="email"
                              placeholder="Email address"
                              id="email"
                              name="email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        </div>
                        <div className="d-flex flex-row align-items-center mb-4">
                          <i className="fas fa-lock fa-lg me-3 fa-fw" />
                          <div className="form-outline flex-fill mb-0">
                            <Input
                              type="password"
                              placeholder="Password"
                              value={formik.values.password}
                              id="password" name="password"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        </div>
                        <div className="d-flex flex-row align-items-center mb-4">
                          <i className="fas fa-key fa-lg me-3 fa-fw" />
                          <div className="form-outline flex-fill mb-0">
                            <Input
                              type="password"
                              placeholder="Confirm password"
                              value={formik.values.confirmPassword}
                              id="confirmPassword" name="confirmPassword"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        </div>
                        <div className="form-check d-flex mb-2">
                          <Checkbox
                            value={formik.values.promotionalEmailsConsent}
                            onChange={(e) => formik.setFieldValue('promotionalEmailsConsent', e.target.checked)}
                          >
                            I'd like to stay up-to-date by email
                          </Checkbox>
                        </div>
                        <div className="form-check d-flex justify-content-center mb-2">
                          <Text color='grey.500' fontSize='sm'>
                            By creating an account you agree to our <Link href='/terms' color='blue.400'>terms of service</Link> and <Link href='/privacy' color='blue.400'> privacy policy</Link>.
                          </Text>
                        </div>
                        <div className="d-flex justify-content-center mx-4 mb-3 mb-lg-4">
                          <button type="submit" className="btn btn-primary btn-lg">Register</button>
                        </div>
                      </form>
                    </div>
                    <div className="col-md-10 col-lg-6 col-xl-7 d-flex align-items-center order-1 order-lg-2">
                      <img src={Map1Image} className="img-fluid" alt="Sample image" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
